import { DataGrid, type GridColumn } from "@/components/data-grid";
import { ChevronLeftIcon } from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";

import { errorMessage } from "../lib/query.ts";

import {
  batchQuery,
  batchStatusLabel,
  points,
  type BatchKpi,
} from "./batches.ts";

/** Fuera del componente: dentro cambiarían de identidad en cada render. */
const KPI_COLUMNS: readonly GridColumn<BatchKpi>[] = [
  {
    key: "kpiCode",
    label: "KPI",
    render: (kpi) => (
      <span>
        <span className="font-mono text-xs text-muted-foreground">
          {kpi.kpiCode}
        </span>{" "}
        {kpi.kpiName}
      </span>
    ),
    sortValue: (kpi) => kpi.kpiName,
  },
  {
    key: "resultCount",
    label: "Resultados",
    numeric: true,
    render: (kpi) => kpi.resultCount,
  },
  {
    key: "totalPoints",
    label: "Puntos",
    numeric: true,
    render: (kpi) => points(kpi.totalPoints),
    sortValue: (kpi) => kpi.totalPoints,
  },
  {
    key: "averageCompliance",
    label: "Cumplimiento medio",
    numeric: true,
    render: (kpi) =>
      kpi.averageCompliance === null
        ? "—"
        : `${(kpi.averageCompliance * 100).toFixed(1)} %`,
    sortValue: (kpi) => kpi.averageCompliance,
  },
];

/**
 * El detalle de un lote. Sin entrada de menú: se llega desde el Resumen o
 * desde una alerta, que es de donde viene la pregunta.
 *
 * Solo se mira. Marcar listo, aplicar o descartar son comandos con
 * confirmación (§8.2) y se hacen hablando con el agente.
 */
export function BatchPanel() {
  const { batchId } = useParams({
    // `_authenticated` es la ruta sin segmento que exige sesión.
    from: "/_authenticated/work/lotes/$batchId",
  });
  const batch = useQuery(batchQuery(batchId));
  const open = batch.data;

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      <Button variant="ghost" size="sm" className="w-fit" asChild>
        <Link to="/work" search={(previous) => previous}>
          <ChevronLeftIcon /> Resumen
        </Link>
      </Button>

      {batch.error != null && (
        <Alert variant="destructive">
          <AlertTitle>No se pudo cargar el lote</AlertTitle>
          <AlertDescription>{errorMessage(batch.error)}</AlertDescription>
        </Alert>
      )}

      {open === undefined ? (
        batch.isPending && (
          <p className="text-sm text-muted-foreground">Cargando el lote…</p>
        )
      ) : (
        <>
          <header>
            <h2 className="flex items-center gap-2 text-xl font-semibold">
              {open.label}
              <Badge variant="outline">{batchStatusLabel(open.status)}</Badge>
            </h2>
            <p className="text-sm text-muted-foreground">
              <span className="font-mono">{open.period}</span> · lo abrió{" "}
              {open.createdBy} el{" "}
              {new Date(open.createdAt).toLocaleDateString("es")}
              {open.appliedAt !== null && (
                <>
                  {" · aplicado el "}
                  {new Date(open.appliedAt).toLocaleDateString("es")}
                </>
              )}
            </p>
          </header>

          <dl className="grid gap-px overflow-hidden rounded-md border text-sm sm:grid-cols-3">
            <Fact label="Resultados" value={points(open.resultCount)} />
            <Fact label="Personas" value={points(open.participantCount)} />
            <Fact label="Puntos" value={points(open.totalPoints)} />
          </dl>

          <h3 className="font-semibold">Por KPI</h3>
          <DataGrid
            columns={KPI_COLUMNS}
            rows={open.kpis}
            empty="El lote no tiene resultados todavía."
          />
        </>
      )}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-secondary/40 px-3 py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold">{value}</dd>
    </div>
  );
}
